import { useRef, useState } from "react";
import { toast } from "sonner";
import { Microphone, Stop, X, CheckCircle, Trash } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

const CATS = ["paint","plumbing","electrical","flooring","drywall","fixtures","cleaning","appliance","exterior","landscaping","hvac","safety","other"];
const PRIOS = ["low","med","high","urgent"];
const PRIO_BG = { low:"bg-slate-100 text-slate-600", med:"bg-amber-100 text-amber-700", high:"bg-red-100 text-red-700", urgent:"bg-pink-500 text-white" };

/**
 * VoiceToPunchModal — mic in, multi-unit punch items out.
 * Records audio → POSTs to /ai/voice-to-project-parse/{projectId} → shows
 * editable preview grid → "Add All" commits each row via the existing
 * /projects/{id}/punch endpoint (per-item so backend validation catches any
 * bad category/priority).
 */
export default function VoiceToPunchModal({ projectId, projectName, rooms = [], defaultRoom = "", onClose, onAdded }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | busy | preview | committing
  const [transcript, setTranscript] = useState("");
  const [items, setItems] = useState([]);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    if (!navigator.mediaDevices) { toast.error("Microphone not supported"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => stream.getTracks().forEach(t => t.stop());
      mr.start();
      mediaRef.current = mr;
      setPhase("recording");
    } catch (e) { toast.error("Mic permission denied"); }
  };

  const stop = async () => {
    const mr = mediaRef.current;
    if (!mr) return;
    mr.stop();
    setPhase("busy");
    await new Promise(r => setTimeout(r, 300));
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    try {
      const fd = new FormData();
      fd.append("file", blob, "voice.webm");
      const r = await api.post(`/ai/voice-to-project-parse/${projectId}`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      setTranscript(r.data?.transcript || "");
      // Fill any AI-empty room with the caller's defaultRoom (unit-page use case)
      const parsedItems = (r.data?.items || []).map((it, i) => ({
        ...it,
        room: (it.room && it.room.trim()) ? it.room : defaultRoom,
        _id: i,
      }));
      setItems(parsedItems);
      setPhase("preview");
      if (!parsedItems.length) toast.error("Radar didn't hear any items — try again.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Voice parse failed");
      setPhase("idle");
    }
  };

  const removeItem = (id) => setItems(items.filter(it => it._id !== id));
  const patchItem = (id, patch) => setItems(items.map(it => it._id === id ? { ...it, ...patch } : it));

  const commitAll = async () => {
    if (!items.length) { toast.error("Nothing to add"); return; }
    setPhase("committing");
    let ok = 0, fail = 0;
    for (const it of items) {
      try {
        await api.post(`/projects/${projectId}/punch`, {
          title: it.title, room: it.room || "", category: it.category || "other",
          priority: it.priority || "med", description: it.description || "",
        });
        ok += 1;
      } catch { fail += 1; }
    }
    if (fail === 0) toast.success(`${ok} punch items added`);
    else toast.warning(`${ok} added · ${fail} failed`);
    onAdded?.();
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" data-testid="voice-punch-modal">
      <div className="w-full sm:max-w-3xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b-2 border-slate-200 flex items-center gap-2 sticky top-0 bg-white z-10">
          <Microphone weight="fill" size={22} className="text-fuchsia-600"/>
          <div className="min-w-0 flex-1">
            <div className="font-display font-black text-lg uppercase tracking-tight" data-testid="voice-punch-title">
              Voice · Add Punch Items
            </div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 truncate">→ {projectName || "Project"}</div>
          </div>
          <button onClick={onClose} data-testid="voice-punch-close"
            className="h-9 w-9 flex items-center justify-center hover:bg-slate-100 rounded"><X size={20}/></button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {phase === "idle" && (
            <div className="text-center py-8">
              <p className="text-sm text-slate-600 max-w-md mx-auto">
                Tap the mic. Say something like: <i>"9A smoke detector. 9B tub stopper. 8B tub stopper and rehang blind. 3A no stove outlet."</i>
              </p>
              <button onClick={start} data-testid="voice-punch-start"
                className="mt-6 h-24 w-24 bg-fuchsia-600 text-white rounded-full inline-flex flex-col items-center justify-center hover:opacity-90 shadow-xl">
                <Microphone weight="fill" size={38}/>
                <span className="text-[9px] font-black uppercase tracking-widest mt-0.5">Tap to Talk</span>
              </button>
            </div>
          )}
          {phase === "recording" && (
            <div className="text-center py-8">
              <button onClick={stop} data-testid="voice-punch-stop"
                className="h-24 w-24 bg-red-600 text-white rounded-full inline-flex flex-col items-center justify-center animate-pulse shadow-xl">
                <Stop weight="fill" size={38}/>
                <span className="text-[9px] font-black uppercase tracking-widest mt-0.5">Stop</span>
              </button>
              <div className="mt-3 text-xs font-black uppercase tracking-widest text-slate-500">Recording… tap to stop</div>
            </div>
          )}
          {phase === "busy" && (
            <div className="text-center py-8 text-sm font-black uppercase tracking-widest text-slate-500">Radar is listening…</div>
          )}
          {phase === "preview" && (
            <>
              {transcript && (
                <div className="mb-4 p-3 bg-slate-50 border-l-4 border-fuchsia-500 text-xs italic text-slate-700" data-testid="voice-punch-transcript">
                  "{transcript}"
                </div>
              )}
              {items.length === 0 && (
                <div className="text-center py-6 text-sm text-slate-500 font-bold" data-testid="voice-punch-empty">
                  Radar didn't extract any items. Tap the mic and try again.
                  <button onClick={start} className="ml-2 underline">Retry</button>
                </div>
              )}
              {items.length > 0 && (
                <div className="space-y-2" data-testid="voice-punch-items">
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
                    Preview · Edit before saving · {items.length} item{items.length === 1 ? "" : "s"}
                  </div>
                  {items.map(it => (
                    <div key={it._id} data-testid={`voice-punch-item-${it._id}`}
                      className="p-3 border-2 border-slate-300 bg-white grid grid-cols-12 gap-2 items-start">
                      <input value={it.room} onChange={e => patchItem(it._id, { room: e.target.value })}
                        list={`voice-rooms-${it._id}`} placeholder="Unit"
                        data-testid={`voice-punch-room-${it._id}`}
                        className="col-span-3 sm:col-span-2 h-9 px-2 border border-slate-300 font-mono font-black text-sm uppercase"/>
                      {rooms.length > 0 && (
                        <datalist id={`voice-rooms-${it._id}`}>{rooms.map(r => <option key={r} value={r}/>)}</datalist>
                      )}
                      <input value={it.title} onChange={e => patchItem(it._id, { title: e.target.value })}
                        placeholder="Title"
                        data-testid={`voice-punch-title-${it._id}`}
                        className="col-span-9 sm:col-span-5 h-9 px-2 border border-slate-300 font-bold text-sm"/>
                      <select value={it.category} onChange={e => patchItem(it._id, { category: e.target.value })}
                        data-testid={`voice-punch-cat-${it._id}`}
                        className="col-span-6 sm:col-span-2 h-9 px-1 border border-slate-300 text-xs font-bold">
                        {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <select value={it.priority} onChange={e => patchItem(it._id, { priority: e.target.value })}
                        data-testid={`voice-punch-prio-${it._id}`}
                        className={`col-span-5 sm:col-span-2 h-9 px-1 border border-slate-300 text-xs font-bold ${PRIO_BG[it.priority] || ""}`}>
                        {PRIOS.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                      <button onClick={() => removeItem(it._id)}
                        data-testid={`voice-punch-remove-${it._id}`}
                        title="Remove"
                        className="col-span-1 h-9 flex items-center justify-center text-slate-400 hover:text-red-600">
                        <Trash size={16}/>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {phase === "committing" && (
            <div className="text-center py-8 text-sm font-black uppercase tracking-widest text-slate-500">Saving {items.length} item{items.length === 1 ? "" : "s"}…</div>
          )}
        </div>

        {/* Footer */}
        {phase === "preview" && items.length > 0 && (
          <div className="p-3 border-t-2 border-slate-200 flex gap-2 sticky bottom-0 bg-white">
            <button onClick={start} data-testid="voice-punch-redo"
              className="h-11 px-4 border-2 border-slate-300 font-black uppercase text-[10px] tracking-widest hover:bg-slate-50">
              Redo
            </button>
            <button onClick={commitAll} data-testid="voice-punch-commit"
              className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-2">
              <CheckCircle weight="fill" size={14}/> Add {items.length} to Project
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
